import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Load, LoadSearchResult } from '../models/load.model';
import { Customer } from '../models/customer.model';
import { FedexFacility } from '../models/fedex-facility.model';
import { LoadOptions } from '../models/load-options.model';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SpotsService {
  private apiUrl = environment.apiEndpoint;

  constructor(private http: HttpClient) { }

  /**
   * Get all loads for an organization
   * @param date Optional date filter in YYYY-MM-DD format
   */
  getLoads(organizationId: string, date?: string): Observable<Load[]> {
    let url = `${this.apiUrl}/organizations/${organizationId}/dispatch/loads`;
    if (date) {
      url += `?date=${date}`;
    }

    return this.http.get<any[]>(url).pipe(
      map(loads => (loads || []).map(load => ({
        ...load,
        id: String(load.id),
        customerId: load.customerId,
        driverEmployeeId: load.driverEmployeeId,
        vehicleNumber: load.vehicleNumber ? String(load.vehicleNumber) : undefined,
        fedexFacilityDropoff: load.fedexFacilityDropoff,
        pickupAvailableTime: load.pickupAvailableTime,
        pickupDeadlineTime: load.pickupDeadlineTime
      })))
    );
  }

  /**
   * Get a specific load by ID
   */
  getLoadById(organizationId: string, loadId: string): Observable<Load> {
    return this.http.get<Load>(`${this.apiUrl}/organizations/${organizationId}/dispatch/loads/${loadId}`);
  }

  /**
   * Add a new load
   */
  addLoad(organizationId: string, load: Load): Observable<Load> {
    return this.http.post<Load>(`${this.apiUrl}/organizations/${organizationId}/dispatch/loads`, load);
  }

  /**
   * Add multiple loads in bulk
   * Uses the /save-loads-bulk endpoint which accepts and returns a list of loads
   */
  addLoadsBulk(organizationId: string, loads: Load[]): Observable<Load[]> {
    return this.http.post<Load[]>(`${this.apiUrl}/organizations/${organizationId}/dispatch/loads/save-loads-bulk`, loads);
  }

  /**
   * Update an existing load
   */
  updateLoad(organizationId: string, loadId: string, load: Partial<Load>): Observable<Load> {
    return this.http.put<Load>(`${this.apiUrl}/organizations/${organizationId}/dispatch/loads/${loadId}`, load);
  }

  /**
   * Delete a load
   */
  deleteLoad(organizationId: string, loadId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/organizations/${organizationId}/dispatch/loads/${loadId}`);
  }

  /**
   * Send email for a load
   */
  sendLoadEmail(organizationId: string, emailPayload: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/organizations/${organizationId}/dispatch/loads/send-email`, emailPayload);
  }

  // ==================== Customer Operations ====================

  /**
   * Get all customers for an organization
   */
  getCustomers(organizationId: string): Observable<Customer[]> {
    return this.http.get<any[]>(`${this.apiUrl}/organizations/${organizationId}/dispatch/customers`).pipe(
      map(customers => (customers || []).map(customer => ({
        ...customer,
        id: Number(customer.id)
      })))
    );
  }

  /**
   * Add a new customer
   */
  addCustomer(organizationId: string, customer: Customer): Observable<Customer> {
    // Prepare payload - exclude fields that backend generates or defaults
    const { id, createdDate, totalLoads, activeLoads, contractLoads, status, ...payload } = customer;

    return this.http.post<any>(`${this.apiUrl}/organizations/${organizationId}/dispatch/customers`, payload).pipe(
      map(response => ({
        ...response,
        id: Number(response.id)
      }))
    );
  }

  /**
   * Update an existing customer
   */
  updateCustomer(organizationId: string, customerId: number, customer: Partial<Customer>): Observable<Customer> {
    return this.http.put<any>(`${this.apiUrl}/organizations/${organizationId}/dispatch/customers/${customerId}`, customer).pipe(
      map(response => ({
        ...response,
        id: Number(response.id)
      }))
    );
  }

  /**
   * Relatively adjust a customer's empty-trailer delta (delta may be negative).
   */
  adjustCustomerEmptyTrailers(organizationId: string, customerId: number, delta: number): Observable<{ id: number; emptyTrailersDelta: number }> {
    return this.http.post<{ id: number; emptyTrailersDelta: number }>(
      `${this.apiUrl}/organizations/${organizationId}/dispatch/customers/${customerId}/adjust-empty-trailers`,
      { delta }
    );
  }

  /**
   * Relatively adjust a customer's actual trailer count (delta may be negative).
   * Independent of the empty-trailer delta — adjusting one never affects the other.
   */
  adjustCustomerActualTrailerCount(organizationId: string, customerId: number, delta: number): Observable<{ id: number; actualTrailerCount: number }> {
    return this.http.post<{ id: number; actualTrailerCount: number }>(
      `${this.apiUrl}/organizations/${organizationId}/dispatch/customers/${customerId}/adjust-actual-trailer-count`,
      { delta }
    );
  }

  /**
   * Explicitly unset a customer's actual trailer count back to null ("not tracked").
   */
  clearCustomerActualTrailerCount(organizationId: string, customerId: number): Observable<{ id: number; actualTrailerCount: number | null }> {
    return this.http.post<{ id: number; actualTrailerCount: number | null }>(
      `${this.apiUrl}/organizations/${organizationId}/dispatch/customers/${customerId}/adjust-actual-trailer-count`,
      { clear: true }
    );
  }

  /**
   * Per-customer pending empty-trailer delta from loads not yet consumed by the batch,
   * split into completed loads (load_date set) and open loads (load_date null).
   */
  getEmptyTrailerAdjustments(organizationId: string): Observable<{ completed: { customerId: number; pendingDelta: number }[]; open: { customerId: number; pendingDelta: number }[] }> {
    return this.http.post<{ completed: { customerId: number; pendingDelta: number }[]; open: { customerId: number; pendingDelta: number }[] }>(
      `${this.apiUrl}/organizations/${organizationId}/dispatch/loads/get-empty-trailer-adjustments`,
      {}
    );
  }

  /**
   * Delete a customer
   */
  deleteCustomer(organizationId: string, customerId: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/organizations/${organizationId}/dispatch/customers/${customerId}`);
  }

  // ==================== FedEx Facility Operations ====================

  /**
   * Get all FedEx facilities for an organization
   */
  getFedexFacilities(organizationId: string): Observable<FedexFacility[]> {
    const url = `${this.apiUrl}/organizations/${organizationId}/dispatch/fedex-facilities`;
    return this.http.get<FedexFacility[]>(url).pipe(
      map(facilities => facilities || [])
    );
  }

  /**
   * Add a new FedEx facility
   */
  addFedexFacility(organizationId: string, facility: FedexFacility): Observable<FedexFacility> {
    // Prepare payload - exclude fields that backend generates
    const { id, organizationId: _, modifiedAt, ...payload } = facility;
    const url = `${this.apiUrl}/organizations/${organizationId}/dispatch/fedex-facilities`;
    return this.http.post<FedexFacility>(url, payload);
  }

  /**
   * Update an existing FedEx facility
   */
  updateFedexFacility(organizationId: string, facilityId: number, facility: Partial<FedexFacility>): Observable<FedexFacility> {
    const url = `${this.apiUrl}/organizations/${organizationId}/dispatch/fedex-facilities/${facilityId}`;
    return this.http.put<FedexFacility>(url, facility);
  }

  /**
   * Delete a FedEx facility
   */
  deleteFedexFacility(organizationId: string, facilityId: number): Observable<void> {
    const url = `${this.apiUrl}/organizations/${organizationId}/dispatch/fedex-facilities/${facilityId}`;
    return this.http.delete<void>(url);
  }

  /**
   * Get available load options (priorities and types)
   */
  getLoadOptions(organizationId: string, returnDefaultsIfMissing: boolean = false): Observable<LoadOptions> {
    let url = `${this.apiUrl}/organizations/${organizationId}/dispatch/loads/get-available-options`;
    if (returnDefaultsIfMissing) {
      url += `?returnDefaultsIfMissing=true`;
    }
    return this.http.post<any>(url, {}).pipe(
      map(response => ({
        loadPriorities: response.loadPriorities || [],
        loadTypes: response.loadTypes || [],
        fedexAvrs: response.fedexAvrs || {},
        bobTailReasons: response.bobTailReasons || [],
        loadDate: response.loadDate
      }))
    );
  }

  /**
   * Update load options (priorities and types colors)
   */
  updateLoadOptions(organizationId: string, options: any): Observable<void> {
    const url = `${this.apiUrl}/organizations/${organizationId}/dispatch/loads/update-load-settings`;
    return this.http.post<void>(url, options);
  }

  searchLoads(organizationId: string, searchBody: { startDate: string; endDate: string }): Observable<LoadSearchResult[]> {
    const url = `${this.apiUrl}/organizations/${organizationId}/dispatch/loads/search`;
    return this.http.post<LoadSearchResult[]>(url, searchBody);
  }

  /**
   * Update an existing FedEx AVR
   */
  updateFedexAvr(organizationId: string, key: string, fedexAvr: any): Observable<void> {
    const url = `${this.apiUrl}/organizations/${organizationId}/dispatch/fedex-avrs/${key}`;
    return this.http.put<void>(url, fedexAvr);
  }

  /**
   * Get load activities/history for a specific load
   */
  getUploadedFiles(organizationId: string, loadId: string, clientId: string, loadFiles: any[]): Observable<any[]> {
    const url = `${this.apiUrl}/organizations/${organizationId}/dispatch/loads/get-uploaded-files`;
    return this.http.post<any[]>(url, { loadId, clientId, loadFiles });
  }

  getLoadActivities(organizationId: string, loadId: string): Observable<any[]> {
    const url = `${this.apiUrl}/organizations/${organizationId}/dispatch/loads/${loadId}/activity-loads`;
    return this.http.get<any[]>(url).pipe(
      map(activities => activities || [])
    );
  }

  /**
   * Get drivers with load counts for a specific date
   */
  getDriversWithLoadCounts(organizationId: string, loadDate: string): Observable<any[]> {
    const url = `${this.apiUrl}/organizations/${organizationId}/dispatch/loads/get-drivers-by-load-counts`;
    return this.http.post<any[]>(url, { loadDate });
  }

}
